/* eslint-disable max-lines */
import pattern from '../../../../common/js/validators/pattern';
import accountNumberLength from '../../../../common/js/validators/account-number-length';
import datePicker from '../../../../common/components/date-picker/date-picker';
import divisibleByNumber from '../../../../common/js/validators/divisible-by-number';
import mobilephoneInput from '../../../../common/components/mobilephone-input/mobilephone-input.vue';
import { required, maxLength, between } from 'vuelidate/lib/validators';
import { mapState } from 'vuex';
import { MOBILE_PHONE_REGEXP } from '../../../../common/js/constants/constants.js';
import debounce from 'lodash-es/debounce';
import { OTP_BANK_ACCOUNT_NUMBER_PREFIX, DEVIZA_BANK_ACCOUNT_NUMBER_PATTERN } from '../../../../common/js/constants/constants';

import VueInput from '@ui-common/vue-input';
import VueErrorMessage from '@ui-common/vue-error-message';
import { VueSelect, VueSelectOption } from '@ui-common/vue-select';
import VueMaskedInput from '@ui-common/vue-masked-input';
import VueCurrencyInput from '@ui-common/vue-currency-input';
import VueCheckbox from '@ui-common/vue-checkbox';
import VueMobilephoneInput from '@ui-common/vue-mobilephone-input';
import { VueCombobox, VueComboboxOption } from '@ui-common/vue-combobox';

const NAME_ON_CARD_MAXLENGHT = 20;
const FORINT = 'Ft';
const HUF = 'huf';

export default {
	data() {
		return {
			model: {
				nameOnCard: '',
				educationalInstitute: '',
				bankAccountNumber: '',
				atmLimit: '',
				purchaseLimit: '',
				purchaseLimitEqualsBalance: false,
				internetLimit: '',
				internetLimitEqualsBalance: false,
				mobilePhoneNumberForConfirmation: '',
				summarizedDailyLimit: '',
				usageScope: null
			},
			nameOnCardMaxlenght: NAME_ON_CARD_MAXLENGHT
		};
	},
	props: {
		usageScopes: {
			required: true
		},
		schools: {
			required: true
		},
		cardData: {
			required: true
		},
		hasClaimCode: {
			required: true
		},
		financialData: {
			required: true
		},
		phoneNumber: {
			required: true
		},
		swImgPath: {
			required: true
		},
		isDevizaCard: {
			required: true
		}
	},
	components: {
		datePicker,
		mobilephoneInput,
		VueInput,
		VueErrorMessage,
		VueSelect,
		VueSelectOption,
		VueMaskedInput,
		VueCurrencyInput,
		VueCheckbox,
		VueMobilephoneInput,
		VueCombobox,
		VueComboboxOption
	},

	validations() {
		let model = {};
		model.nameOnCard = { required, maxLength: maxLength(NAME_ON_CARD_MAXLENGHT) };
		model.educationalInstitute = this.cardData.isic ? { required } : {};
		model.bankAccountNumber = this.isDevizaCard
			? {
					required,
					accountNumberLength: accountNumberLength,
					devizaAccountNumberFormat: pattern(DEVIZA_BANK_ACCOUNT_NUMBER_PATTERN)
			  }
			: {
					required,
					accountNumberLength: accountNumberLength
			  };
		model.atmLimit = this.atmLimitRequired
			? {
					required,
					between: between(this.cardData.atmLimitMin, this.cardData.atmLimitMax == 0 ? Infinity : this.cardData.atmLimitMax),
					divisibleByNumber: divisibleByNumber(this.cardData.limitDivisibility)
			  }
			: {};
		model.purchaseLimit =
			this.purchaseLimitRequired && !this.model.purchaseLimitEqualsBalance
				? {
						required,
						between: between(this.cardData.purchaseLimitMin, this.cardData.purchaseLimitMax == 0 ? Infinity : this.cardData.purchaseLimitMax),
						divisibleByNumber: divisibleByNumber(this.cardData.limitDivisibility)
				  }
				: {};
		model.internetLimit =
			this.internetLimitRequired && !this.model.internetLimitEqualsBalance
				? {
						required,
						between: between(this.cardData.internetLimitMin, this.cardData.internetLimitMax == 0 ? Infinity : this.cardData.internetLimitMax),
						divisibleByNumber: divisibleByNumber(this.cardData.limitDivisibility)
				  }
				: {};
		model.mobilePhoneNumberForConfirmation =
			this.model.internetLimitEqualsBalance || this.model.internetLimit
				? {
						required,
						pattern: pattern(MOBILE_PHONE_REGEXP)
				  }
				: {};
		model.summarizedDailyLimit = this.summarizedDailyLimitRequired
			? {
					required,
					between: between(this.cardData.juniorLimitMin, this.cardData.juniorLimitMax == 0 ? Infinity : this.cardData.juniorLimitMax),
					divisibleByNumber: divisibleByNumber(this.cardData.limitDivisibility)
			  }
			: {};
		model.usageScope = !this.hasClaimCode && !this.cardData.usageScopeDisabled ? { required } : {};
		return { model };
	},
	watch: {
		'model.purchaseLimitEqualsBalance'(value) {
			if (value) {
				this.$v.model.purchaseLimit.$reset();
				this.model.purchaseLimit = null;
			}
		},
		'model.internetLimitEqualsBalance'(value) {
			if (value) {
				this.$v.model.internetLimit.$reset();
				this.model.internetLimit = null;
			}
		}
	},
	computed: {
		...mapState(['currency']),
		isMobile() {
			return this.$resize && this.$mq.below(767);
		},
		atmLimitRequired() {
			return this.cardData.atmLimitMin !== null && this.cardData.atmLimitMax !== null && !this.hasClaimCode;
		},
		purchaseLimitRequired() {
			return this.cardData.purchaseLimitMin !== null && this.cardData.purchaseLimitMax !== null && !this.hasClaimCode;
		},
		internetLimitRequired() {
			return this.cardData.internetLimitMin !== null && this.cardData.internetLimitMax !== null && !this.hasClaimCode;
		},
		summarizedDailyLimitRequired() {
			return this.cardData.juniorLimitMin !== null && this.cardData.juniorLimitMax !== null && !this.hasClaimCode;
		},
		...mapState({
			fullName: (state) => state.personal.personalData.fullName
		}),
		currentCurrency() {
			return this.currency.toLowerCase() == HUF ? FORINT : this.currency;
		},
		atmDailyLimit() {
			return (
				this.$options.filters.currency(this.cardData.atmLimitMin, 'HUF') +
				' - ' +
				this.$options.filters.currency(this.cardData.atmLimitMax, 'HUF') +
				' ' +
				this.currentCurrency +
				' közötti, ' +
				this.$options.filters.currency(this.cardData.limitDivisibility, 'HUF') +
				'-' +
				this.inflect(this.cardData.limitDivisibility) +
				' osztható összeget írjon be.'
			);
		},
		dailySpendingLimit() {
			return (
				this.$options.filters.currency(this.cardData.purchaseLimitMin, 'HUF') +
				' vagy ' +
				this.$options.filters.currency(this.cardData.purchaseLimitMin, 'HUF') +
				' ' +
				this.currentCurrency +
				' feletti, ' +
				this.$options.filters.currency(this.cardData.limitDivisibility, 'HUF') +
				'-' +
				this.inflect(this.cardData.limitDivisibility) +
				' osztható összeget írjon be.'
			);
		},
		virtualDailySpendingLimit() {
			return (
				this.$options.filters.currency(this.cardData.internetLimitMin, 'HUF') +
				' vagy ' +
				this.$options.filters.currency(this.cardData.internetLimitMin, 'HUF') +
				' ' +
				this.currentCurrency +
				' feletti, ' +
				this.$options.filters.currency(this.cardData.limitDivisibility, 'HUF') +
				'-' +
				this.inflect(this.cardData.limitDivisibility) +
				' osztható összeget írjon be.'
			);
		},
		totalDailyLimit() {
			return (
				this.$options.filters.currency(this.cardData.juniorLimitMin, 'HUF') +
				' - ' +
				this.$options.filters.currency(this.cardData.juniorLimitMax, 'HUF') +
				' ' +
				this.currentCurrency +
				' közötti, ' +
				this.$options.filters.currency(this.cardData.limitDivisibility, 'HUF') +
				'-' +
				this.inflect(this.cardData.limitDivisibility) +
				' osztható összeget írjon be.'
			);
		}
	},
	methods: {
		delayTouch(error) {
			setTimeout(() => {
				error.$touch();
			}, 500);
		},
		inflect(val) {
			let affix = '';
			if (!Number.isNaN(val)) {
				switch (val) {
					case 1:
						affix = 'gyel';
						break;
					case 10:
						affix = 'zel';
						break;
					case 500:
						affix = 'zal';
						break;
					case 10000:
						affix = 'rel';
						break;
					default:
						affix = 'val';
				}
			}
			return affix;
		},
		isValid() {
			this.$v.$touch();
			return !this.$v.$invalid;
		},
		setAndLookupSchools(value) {
			this.lookupSchools(value.trim());
		},
		lookupSchools: debounce(function (value) {
			value = value.trim();
			if (value && value.length >= 2) {
				this.$emit('on-lookup-schools', value);
			} else {
				this.$emit('on-reset-schools', value);
			}
		}, 300),
		removeInvalidChars() {
			this.model.nameOnCard = this.model.nameOnCard.replace ? this.model.nameOnCard.replace(/'/g, '') : this.model.nameOnCard;
		},
		savePageData() {
			this.$v.$touch();
			if (this.isValid()) {
				let modelToSend = this.convertToBackendModel();
				this.$emit('on-save-financial-data', modelToSend);
			} else {
				this.$nextTick(() => {
					let elem = document.querySelector('.has-error');
					if (elem) {
						this.$scrollTo(elem, { duration: 500 });
					}
				});
			}
		},
		convertToFrontendModel(val) {
			let model = Object.assign({}, this.model, val);
			model.bankAccountNumber = model.bankAccountNumber.substring(OTP_BANK_ACCOUNT_NUMBER_PREFIX.length);
			this.model = model;
		},
		convertToBackendModel() {
			let model = Object.assign({}, this.model);
			model.bankAccountNumber = OTP_BANK_ACCOUNT_NUMBER_PREFIX + model.bankAccountNumber;
			return model;
		}
	},
	created() {
		this.model.mobilePhoneNumberForConfirmation = this.phoneNumber;
		if (this.financialData) {
			this.convertToFrontendModel(this.financialData);
		}
		if (!this.model.mobilePhoneNumberForConfirmation && !this.hasClaimCode && this.phoneNumber) {
			this.model.mobilePhoneNumberForConfirmation = this.phoneNumber;
		}
		if (!this.model.nameOnCard && this.fullName && this.fullName.length && this.fullName.length <= NAME_ON_CARD_MAXLENGHT) {
			this.model.nameOnCard = this.fullName;
			this.removeInvalidChars();
		}
	}
};

/* eslint-enable max-lines */
